import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect } from "react";
import { Link, Routes, Route, useNavigate } from "react-router-dom";
import { axiosInstance, useAuth } from "@/pages/auth/auth.context";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { User } from "@/pages/admin/employees/types";
import { Loading } from "@/components/ui/loading";
import EmployeeCertifications from "@/pages/EmployeeCertifications";
import { useToast } from "@/hooks/use-toast";
import InputMask from "react-input-mask";
import { CitySelector } from "@/components/ui/city-selector";

const Profile = () => {
  const [formData, setFormData] = useState<User | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  const endpoint = `${process.env.REACT_APP_BASE_API_URL}user/me`;

  // Fetch user profile data
  const {
    data: userData,
    isLoading: userLoading,
    error: userError,
  } = useQuery({
    queryKey: ["userProfile"],
    queryFn: async () => {
      const response = await axiosInstance.get<User>(endpoint);
      return response.data;
    },
    meta: {
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to load your profile",
          variant: "destructive",
        });
      },
    },
  });

  useEffect(() => {
    if (userData) {
      setFormData(userData);
    }
  }, [userData]);

  const updateProfileMutation = useMutation({
    mutationFn: async (updatedData: User) => {
      const response = await axiosInstance.put<User>(endpoint, updatedData);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Profile updated successfully!",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["userProfile"] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData) {
      updateProfileMutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!formData) return;

    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    if (!formData) return;

    setFormData({
      ...formData,
      [field]: checked,
    });
  };

  if (userLoading) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <Loading text="Loading your profile..." />
      </div>
    );
  }

  if (userError || !formData) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-2xl mx-auto">
          <Card className="p-6 shadow-md bg-card">
            <div className="text-center">
              <h3 className="text-lg font-medium">Error loading profile</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Please try refreshing the page
              </p>
              <Button onClick={() => window.location.reload()} className="mt-4">
                Refresh
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // EmployeeCertifications component now fetches its own data
  const renderCertificationsPage = () => <EmployeeCertifications />;

  return (
    <Routes>
      <Route
        path="/"
        element={
          <div className="min-h-screen bg-background p-6 transition-colors">
            <div className="max-w-2xl mx-auto">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h1 className="text-4xl font-bold text-foreground mb-2">
                    Manage Profile
                  </h1>
                  <p className="text-muted-foreground">
                    Update your personal information
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => navigate("certifications")}
                  >
                    View Certifications
                  </Button>
                  <Link to="/change-password">
                    <Button variant="outline">Change Password</Button>
                  </Link>
                </div>
              </div>

              <Card className="p-6 shadow-md bg-card text-card-foreground">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="firstName"
                        className="text-sm font-medium text-foreground"
                      >
                        First Name
                      </label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName || ""}
                        onChange={handleChange}
                        className="bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="lastName"
                        className="text-sm font-medium text-foreground"
                      >
                        Last Name
                      </label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName || ""}
                        onChange={handleChange}
                        className="bg-background"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className="text-sm font-medium text-foreground"
                    >
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email || ""}
                      onChange={handleChange}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="phoneNumber"
                      className="text-sm font-medium text-foreground"
                    >
                      Phone Number
                    </label>
                    <InputMask
                      mask="(999) 999-9999"
                      maskChar="_"
                      value={formData.phoneNumber || ""}
                      onChange={(e) =>
                        handleChange(e as React.ChangeEvent<HTMLInputElement>)
                      }
                    >
                      {(
                        inputProps: React.InputHTMLAttributes<HTMLInputElement>,
                      ) => (
                        <Input
                          {...inputProps}
                          id="phoneNumber"
                          name="phoneNumber"
                          type="tel"
                          placeholder="(555) 555-5555"
                          className="bg-background"
                        />
                      )}
                    </InputMask>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="city"
                      className="text-sm font-medium text-foreground"
                    >
                      City
                    </label>
                    <CitySelector
                      value={formData.city || ""}
                      onValueChange={(value) => {
                        if (!formData) return;
                        setFormData({
                          ...formData,
                          city: value,
                        });
                      }}
                      placeholder="Select your city..."
                      className="bg-background"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="bilingual"
                      checked={formData.bilingual || false}
                      onCheckedChange={(checked) =>
                        handleCheckboxChange("bilingual", checked === true)
                      }
                    />
                    <label
                      htmlFor="bilingual"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Bilingual (Speaks multiple languages)
                    </label>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium text-lg mb-2">
                      Notification Preferences
                    </h3>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="emailNotifications"
                        checked={formData.emailNotifications || false}
                        onCheckedChange={(checked) =>
                          handleCheckboxChange(
                            "emailNotifications",
                            checked === true,
                          )
                        }
                      />
                      <label
                        htmlFor="emailNotifications"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Email Notifications
                      </label>
                    </div>
                    <div className="flex items-center space-x-2 mt-2">
                      <Checkbox
                        id="textNotifications"
                        checked={formData.smsNotifications || false}
                        onCheckedChange={(checked) =>
                          handleCheckboxChange(
                            "smsNotifications",
                            checked === true,
                          )
                        }
                      />
                      <label
                        htmlFor="textNotifications"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Text Message Notifications
                      </label>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mt-2 text-xs text-muted-foreground">
                      <span>
                        By using this application, you agree to our
                        <Link
                          to="/terms"
                          className="text-foreground underline font-semibold hover:text-primary ml-1"
                        >
                          Terms & Conditions
                        </Link>
                        and
                        <Link
                          to="/privacy"
                          className="text-foreground underline font-semibold hover:text-primary ml-1"
                        >
                          Privacy Policy
                        </Link>
                        .
                      </span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={updateProfileMutation.isPending}
                  >
                    {updateProfileMutation.isPending
                      ? "Saving..."
                      : "Save Changes"}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        }
      />
      <Route path="certifications" element={renderCertificationsPage()} />
    </Routes>
  );
};

export default Profile;
